/**
 * @author Wiener Games
 *
 * Resource loader class.
 * Responsible for initial loading of required assets.
 */
import TextField from "../views/components/TextField";
import Utils from "./Utils";
import App from "../App";
import GameModel from "../model/GameModel";
import ConsoleUtil from "./ConsoleUtil";
import { Loader, utils as PixiUtils, BaseTexture, Texture } from "pixi.js";
import { IAddOptions, LoaderResource } from "@pixi/loaders"
import { ConfigurationTypes } from '../const/ConfigurationTypes';
import ConfigManager from '../managers/ConfigManager';
import FConnector from "../../../src/server/FConnector";
interface IFallbackResourceItem {
    tpsName: string;
    tpsFolder: string;
    outputFolder: string;
    orignalTexture: string;
    originalSheet: string;
    fallbackTexture: string;
    fallbackSheet: string
}

enum WebPFeature {
    Lossy = "lossy",
    Lossless = "lossless",
    Alpha = "alpha",
    Animation = "animation"
}

export class GameLoader {
    private pixiLoader: Loader;
    public defaultLang = "en";
    public static readonly LANG_TO_LOCALE: {[locale: string]: string} = {
        en_gb: 'en',
        en_us: 'en',
        en_sc: 'en-sc',
        en_st: 'en-st',
        bg_bg: 'bg',
        cs_cz: 'cs',
        da_dk: 'da',
        de_de: 'de',
        el_gr: 'el',
        es_es: 'es',
        es_mx: 'es-mx',
        et_ee: 'et',
        fi_fi: 'fi',
        fr_fr: 'fr',
        hr_hr: 'hr',
        hu_hu: 'hu',
        id_id: 'id',
        it_it: 'it',
        ja_jp: 'ja',
        ka_ge: 'ka',
        ko_kr: 'ko',
        km_kh: 'km',
        lt_lt: 'lt',
        lv_lv: 'lv',
        ms_my: 'ms',
        nl_be: 'nl-be',
        nl_nl: 'nl',
        nb_no: 'no',
        pl_pl: 'pl',
        pt_pt: 'pt',
        pt_br: 'pt-br',
        ro_ro: 'ro',
        ru_ru: 'ru',
        sk_sk: 'sk',
        sl_si: 'sl',
        sv_se: 'sv',
        sr_sp: 'sr',
        th_th: 'th',
        tr_tr: 'tr',
        uk_ua: 'uk',
        vi_vn: 'vi',
        zh_cn: 'zh-cn',
        zh_tw: 'zh-tw',
    };
    // private urlParams; // TODO it is possible this data to come as http response, not as urlParams
    private version;

    private readonly fallbackConfigFileName: string = "fallback-config.json";
    private fallbackData: Set<IFallbackResourceItem>;
    private useFallbackAssets: boolean = false;

    private baseTextureCache: {
        [key: string]: BaseTexture;
    };

    private textureCache: {
        [key: string]: Texture;
    }

    constructor() {
        this.pixiLoader = Loader.shared;
        this.pixiLoader.use((res, next) => { this.onResourceLoaded(res, next) });
        // this.urlParams = gameModel;
        this.version = GameModel.version;

        for (const key in WebPFeature) {
            this.checkWebpFeature(WebPFeature[key], (feature: WebPFeature, isSupported: boolean) => {
                if (!isSupported) {
                    this.useFallbackAssets = true;
                }

                ConsoleUtil.log(`WebP (${feature}) supported: ${isSupported}`);
            });
        }

        this.baseTextureCache = {};
        this.textureCache = {};
    }
    public static getLocaleFromLang(langCode: string): string {
        const langCodeToLowerCase: string = langCode.toLowerCase();
        if (this.LANG_TO_LOCALE[langCodeToLowerCase]) {
            return this.LANG_TO_LOCALE[langCodeToLowerCase];
        } else if (Object.values(this.LANG_TO_LOCALE).includes(langCodeToLowerCase)) {
            let result = 'en-us';
            Object.entries(this.LANG_TO_LOCALE).forEach((key) => {
                if (key[1] === langCode) {
                    result = key[0];
                };
            });

            return result
        };

        return 'en-us'
    }

    public loadLocalization(localizedAssetList, callback) {
        GameModel.lang = ConfigManager?.getConfig(ConfigurationTypes.LOCAL)?.forceLanguage || GameModel.lang.toLowerCase();
        let underscoreIndex = GameModel.lang.indexOf("_");
        if (underscoreIndex > -1 ) {
            GameModel.lang = GameModel.lang.replace("_", "-");
        } else {
            GameModel.lang = GameLoader.getLocaleFromLang(GameModel.lang).replace("_", "-");
        };

        let localizationName: string;
        try {
            let locale = new Intl.NumberFormat(GameModel.lang).resolvedOptions().locale;
            //let dashIndex = locale.indexOf("-");
            // if (dashIndex > -1) {
            //     locale = locale.substring(0, dashIndex);
            // }

            localizationName = locale;
        } catch (error) {
            localizationName = this.defaultLang;
            GameModel.lang = this.defaultLang;
        }

        if (localizedAssetList[localizationName]) {
            this.queueResources(localizedAssetList[localizationName]);
        } else {
            this.queueResources(localizedAssetList[this.defaultLang]);
        }
        // this.pixiLoader.add("localization", "assets/locale/" + localizationName + ".json");
        this.pixiLoader.load(callback);
    }

    // should be refactored
    public loadFonts(gameConfig: any) {
        let style = document.createElement("style");
        let fontsToLoad = {};
        let fontConfig = {
            fonts: gameConfig.fonts,
            languageFont: gameConfig.fontMap[GameModel.lang],
        };
        let baseUrl = this.pixiLoader.baseUrl ? this.pixiLoader.baseUrl : "./";

        if(baseUrl[baseUrl.length-1]!="/"){
            baseUrl+="/";
        }

        for (let fontName in fontConfig.fonts) {

            fontsToLoad[fontName] = {
                url:
                baseUrl + "assets/fonts/" +
                    fontConfig.fonts[fontName].url +
                    "?version=" +
                    this.version,
                format: fontConfig.fonts[fontName].format
                    ? fontConfig.fonts[fontName].format
                    : "truetype"
            };
            TextField.fontMeasure[fontName] = {
                text:
                    fontConfig.fonts[fontName].measureText ||
                    gameConfig.defaultMeasureText,
                baseline:
                    fontConfig.fonts[fontName].measureBaseline ||
                    gameConfig.defaultMeasureBaseline
            };
        }

        for (let fontName in fontsToLoad) {
            if (fontConfig.languageFont) {
                fontsToLoad[fontName] = {
                    url: "core/assets/fonts/" + fontConfig.languageFont.url,
                    format: fontConfig.languageFont.format
                        ? fontConfig.languageFont.format
                        : "truetype"
                };
                TextField.fontMeasure[fontName] = {
                    text:
                        fontConfig.languageFont.measureText ||
                        gameConfig.defaultMeasureText,
                    baseline:
                        fontConfig.languageFont.measureBaseline ||
                        gameConfig.defaultMeasureBaseline
                };
            }
            style.innerHTML +=
                "@font-face{" +
                "  font-family: " +
                fontName +
                ";" +
                "  src: url('" +
                fontsToLoad[fontName].url +
                "')" +
                "       format('" +
                fontsToLoad[fontName].format +
                "');" +
                "  font-display: block;" +
                "}";

            let p = document.createElement("p");
            p.innerHTML = "abc123";
            p.style.fontFamily = fontName;
            p.style.position = "fixed";
            p.style.bottom = "100%";
            document.getElementsByTagName("body")[0].appendChild(p);
        }
        document.getElementsByTagName("head")[0].appendChild(style);
    }

    public loadAssets(
        assetList,
        callback?: (...args: any[]) => void,
        onProgress?: (...args: any[]) => void
    ) {
        let assetsCopy: any[] = Utils.deepClone(assetList);
        let getNext;
        let assetsCount = assetList.length;

        getNext = () => {
            if (assetsCopy && assetsCopy.length) {
                let asset = assetsCopy.pop();

                this.queueResources([asset]);
                this.pixiLoader.load(() => {
                    this.secondaryLoad(asset[0], getNext);
                });
                if(onProgress) {
                    const progress = 1 - assetsCopy.length / assetsCount;

                    onProgress(progress);

                    if (!GameModel.isReplay) {
                        FConnector.gameLoaded(this.formatValue(progress))
                    }
                }
            } else {
                if (callback) {
                    if (onProgress) {
                        onProgress(100);

                        if (!GameModel.isReplay) {
                            FConnector.gameLoaded(100)
                        }
                    }
                    callback(this.pixiLoader);
                }
            }
        }

        getNext();
    }

    // gameLoaded should receive values > 0...100
    private formatValue(value: number): number {
        if (value <= 1) {
            return Math.trunc(value * 100);
        }

        return Math.trunc(value);
    }

    /**
     * Gets the corrected asset url, in case we must load the fallback resources.
     *
     * @private
     * @param {string} assetKey The asset key, used at asset-config.json, also passed to the pixi loader.
     * @param {string} url The relative path, where the asset is located at the server/filesystem
     * @return {string} The appropriate url, depending if we need fallback resources or not.
     * @memberof GameLoader
     */
    private convertAssetUrl(assetKey: string, url: string): string {
        if (this.fallbackData && this.fallbackData.size > 0 && this.useFallbackAssets && url.endsWith(".json")) {
            // Texture packer spritesheet url always follows this pattern
            // {folder}/{tps_name}-{zero-based-number}.json
            // Examples
            // ./assets/textures/mobile/preloader-0.json
            // assets/textures/desktop/sym_anim-10.json
            const extensionIndex = url.lastIndexOf(".json");
            const fileNameWithoutExtension = url.substring(
                0,
                extensionIndex
            );
            const dashIndex = fileNameWithoutExtension.lastIndexOf("-");
            if (dashIndex == -1) {
                return url;
            }

            const numericPart = url.substring(dashIndex + 1, extensionIndex);
            if (Number.isNaN(parseInt(numericPart))) {
                return url;
            }

            let result = "";
            this.fallbackData.forEach(item => {
                if (result.length == 0) {
                    // Runtime there is no game/static folder
                    const folder = item.outputFolder.replace("game/static/", "");

                    // The fallback data comes with {n} placeholder for the numeric part
                    const originalSheetName = item.originalSheet.replace("{n}", numericPart);

                    const originalSheetPath = folder + "/" + originalSheetName;

                    // Make sure the url is not prefixed with ./
                    if (originalSheetPath == url.replace("./", "")) {
                        result = folder + "/" + item.fallbackSheet.replace("{n}", numericPart);
                    }
                }
            });

            if (result.length == 0) {
                // In case something went wrong, this should also do the trick.
                result = `${fileNameWithoutExtension}-png.json`;
            }

            return result;
        }

        return url;
    }

    private queueResources(resourceList: any, options?: any) {
        for (let i = 0; i < resourceList.length; i++) {
            const path = this.convertAssetUrl(
                resourceList[i][0],
                resourceList[i][1]
            );
            if (this.version) {
                this.add(
                        resourceList[i][0],
                        path + "?version=" + this.version,
                        options
                    );
            } else {
                this.add(resourceList[i][0], path, options);
            }
        }
    }

    public get loader() {
        return this.pixiLoader;
    }

    /**
     * @method loadNewResources
     * Used for loading new resources
     * @param assetConfigKey
     * @type String
     *
     * @param callback function to used after resources are loaded
     * key value from the asset-config.json
     * @description Usage: GameLoader.loadNewResources()
     */
    loadNewResources(assetConfigKey: string, callback ?: (...args: any[]) => void) {
        let config = App.assets.resources.asset_config.data;
        this.resetLoader();

        this.loadConfig(config[assetConfigKey], callback);
        Loader.shared.resources = Object.assign(App.assets.resources, this.pixiLoader.resources);
    }

    public resetLoader() {
        PixiUtils.clearTextureCache();
        this.pixiLoader = new Loader();
    }

    public get config() {
        return this.pixiLoader.resources.asset_config.data;
    }

    public loadConfig(configArr: string[][], callback) {
        this.queueResources(configArr);
        this.pixiLoader.load(callback);
    }

    protected secondaryLoad(
        atlasName: string,
        callback?: (...args: any[]) => void
    ) {
        // Commented out, because the code below loads the multipack images twice!
        // const atlas = this.pixiLoader.resources[atlasName];
        // if (
        //     atlas &&
        //     atlas.data &&
        //     atlas.data.meta &&
        //     atlas.data.meta.related_multi_packs
        // ) {
        //     for (const pack in atlas.data.meta.related_multi_packs) {
        //         let url = this.convertAssetUrl(atlasName, atlas.url);
        //         url = url.slice(0, url.indexOf(atlas.name));
        //         if (this.pixiLoader.baseUrl && this.pixiLoader.baseUrl.length) {
        //             url = url.replace(this.pixiLoader.baseUrl, "");
        //         }
        //         let assetName = atlas.data.meta.related_multi_packs[pack];

        //         if (!this.pixiLoader.resources[assetName]) {
        //             if (this.version) {
        //                 this.add(
        //                         assetName,
        //                         url + assetName + "?version=" + this.version
        //                     );
        //             } else {
        //                 this.add(assetName, url + assetName);
        //             }
        //         }
        //     }
        // }
        callback ? this.pixiLoader.load(callback) : this.pixiLoader.load();
    }


    private add(name: string, url: string, options?: IAddOptions, callback?: LoaderResource.OnCompleteSignal) {
        const isSpineAnimation = name.toLowerCase().startsWith("spine~");
        if (isSpineAnimation && name.toLowerCase().endsWith("~image")) {
            // This is a spine image. The atlas/json will autoload this image,
            // so we do not need to load it twice.
            return;
        }

        if (isSpineAnimation && !this.useFallbackAssets && App.config.hasWebpSpines == true) {
            // Redirecting to the webp subfolder, instead of the root folder.
            const lastSlashIndex = url.lastIndexOf("/");
            const folderPath = url.substring(0, lastSlashIndex);
            url = `${folderPath}/webp${url.substring(lastSlashIndex)}`;
        }

        this.clearPixiCache();
        return this.pixiLoader.add(name, url, options, callback);
    }

    private clearPixiCache() {
        Object.keys(PixiUtils.BaseTextureCache).forEach(key => {
            this.baseTextureCache[key] = PixiUtils.BaseTextureCache[key];
        });

        Object.keys(PixiUtils.TextureCache).forEach(key => {
            this.textureCache[key] = PixiUtils.TextureCache[key];
        });

        PixiUtils.clearTextureCache();
    }

    private restorePixiCache() {
        Object.keys(this.baseTextureCache).forEach(key => {
            PixiUtils.BaseTextureCache[key] = this.baseTextureCache[key];
        });

        Object.keys(this.textureCache).forEach(key => {
            PixiUtils.TextureCache[key] = this.textureCache[key];
        });
    }

    public loadSoundAssets(assetList, callback?: (...args: any[]) => void) {
        const type = { loadType: "XHR", xhrType: "blob" };
        const list = [];
        assetList.forEach(asset => {
            const key = asset[0];
            const value = asset[1];
            if (Array.isArray(value)) {
                value.forEach((item, index) => {
                    list.push([`${key}~track~${index}`, item]);
                });
            } else {
                list.push([key, value]);
            }
        });

        this.queueResources(list, type);
        this.restorePixiCache();
        this.pixiLoader.load(callback);
    }

    private onResourceLoaded(
        resource: LoaderResource,
        next: (...args: any[]) => void
    ) {
        if (this.pixiLoader.resources[resource.name].isComplete && resource.url.endsWith(this.fallbackConfigFileName) && resource.data) {
            if (!this.fallbackData) {
                this.fallbackData = new Set<IFallbackResourceItem>();
            }

            Object.keys(resource.data).forEach(key => {
                const item = resource.data[key] as IFallbackResourceItem;
                this.fallbackData.add(item);
            });
        }

        next();
    }

    /**
     * Detects whether the WebP image format is supported by the browser.
     *
     * @private
     * @param {*} feature can be one of 'lossy', 'lossless', 'alpha' or 'animation'.
     * @param {*} callback callback(feature, isSupported)' will be passed back the detection result (in an asynchronous way!)
     * @memberof GameLoader
     */
    private checkWebpFeature(feature: WebPFeature, callback: (feature: WebPFeature, isSupported: boolean) => void) {
        const kTestImages = {
            lossy: "UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA",
            lossless: "UklGRhoAAABXRUJQVlA4TA0AAAAvAAAAEAcQERGIiP4HAA==",
            alpha: "UklGRkoAAABXRUJQVlA4WAoAAAAQAAAAAAAAAAAAQUxQSAwAAAARBxAR/Q9ERP8DAABWUDggGAAAABQBAJ0BKgEAAQAAAP4AAA3AAP7mtQAAAA==",
            animation:
                "UklGRlIAAABXRUJQVlA4WAoAAAASAAAAAAAAAAAAQU5JTQYAAAD/////AABBTk1GJgAAAAAAAAAAAAAAAAAAAGQAAABWUDhMDQAAAC8AAAAQBxAREYiI/gcA",
        };

        const img = new Image();
        img.onload = function () {
            const result = img.width > 0 && img.height > 0;
            callback(feature, result);
        };

        img.onerror = function () {
            callback(feature, false);
        };

        img.src = "data:image/webp;base64," + kTestImages[feature];
    }
}

export default new GameLoader();
